import * as React from 'react';
import { AlertTriangle, PenLine, RotateCcw, Sparkles, X } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { Callout, WorkspaceButton } from '@/components/ui/workspace';
import { ProposalCompare } from './proposal-compare';
import type { TokenValues } from './tokens';
import {
  PRESET_LABEL,
  REWRITE_PRESETS,
  instructionOf,
  type EditorBlock,
  type EditorCitation,
  type RewriteIssue,
  type RewriteProposal,
  type RewriteRequest,
  type RewriteResult,
} from './types';

/**
 * Rewrite one block by asking (E14-design §5; ADR-025 decision 7): four
 * presets or an instruction in the person's own words. The proposal comes
 * back unsaved and shows beside the current text; Accept saves it as
 * `ai_rewritten` with the instruction, Try again asks the same again,
 * Discard leaves the text as it was. A proposal the guards refused comes
 * back as issues, worded here for a person.
 */

/** A guard's finding in plain words (T-0270's issue kinds). */
export function rewriteIssueText(issue: RewriteIssue): string {
  const named = issue.text ? ` (${issue.text})` : '';
  switch (issue.kind) {
    case 'new_figure':
      return `The rewrite added a figure that isn't in the report${named}; try again or edit by hand.`;
    case 'unknown_token':
      return `The rewrite used a figure name the report doesn't have${named}; try again or edit by hand.`;
    case 'foreign_citation':
      return "The rewrite cited a source this section doesn't draw on; try again or edit by hand.";
    case 'lost_all_citations':
      return 'The rewrite dropped every source this text cites; try again or edit by hand.';
    default:
      return issue.message?.trim()
        ? issue.message
        : "The rewrite didn't pass the report's checks; try again or edit by hand.";
  }
}

export interface RewritePanelProps {
  block: EditorBlock;
  tokens: TokenValues;
  tokenSource?: string;
  onRewrite: (request: RewriteRequest) => Promise<RewriteResult>;
  /** `instruction`: what the audit records, the server's when it said, else the request's. */
  onAccept: (proposal: RewriteProposal, instruction: string) => Promise<unknown>;
  onClose: () => void;
  onEditByHand: () => void;
  onOpenCitation?: (citation: EditorCitation) => void;
}

type Step =
  | { kind: 'ask' }
  | { kind: 'asking'; request: RewriteRequest }
  | { kind: 'answered'; request: RewriteRequest; result: RewriteResult }
  | { kind: 'failed'; request: RewriteRequest; error: unknown };

export function RewritePanel({
  block,
  tokens,
  tokenSource,
  onRewrite,
  onAccept,
  onClose,
  onEditByHand,
  onOpenCitation,
}: RewritePanelProps) {
  const [step, setStep] = React.useState<Step>({ kind: 'ask' });
  const [instruction, setInstruction] = React.useState('');
  const [accepting, setAccepting] = React.useState(false);
  const [acceptError, setAcceptError] = React.useState<unknown>(null);
  // Results that land after unmount are dropped. Set on mount too: a
  // development StrictMode remount runs the cleanup once in between.
  const live = React.useRef(true);
  React.useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const ask = async (request: RewriteRequest) => {
    setAcceptError(null);
    setStep({ kind: 'asking', request });
    try {
      const result = await onRewrite(request);
      if (live.current) setStep({ kind: 'answered', request, result });
    } catch (error) {
      if (live.current) setStep({ kind: 'failed', request, error });
    }
  };

  const accept = async (proposal: RewriteProposal, instruction: string) => {
    setAccepting(true);
    setAcceptError(null);
    try {
      await onAccept(proposal, instruction);
    } catch (error) {
      if (live.current) setAcceptError(error);
    } finally {
      if (live.current) setAccepting(false);
    }
  };

  const asking = step.kind === 'asking';
  const typed = instruction.trim();
  const what = block.kind === 'bullets' ? 'list' : 'paragraph';

  return (
    <div
      className="mt-2 flex flex-col gap-3 rounded-card border border-ai-surface-border bg-ai-surface px-3 py-3"
      data-testid="rewrite-panel"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !accepting) {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-meta font-semibold text-ai-ink">
          <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
          Rewrite this {what}
        </span>
        {REWRITE_PRESETS.map((preset) => (
          <WorkspaceButton
            key={preset}
            disabled={asking || accepting}
            onClick={() => void ask({ preset })}
          >
            {PRESET_LABEL[preset]}
          </WorkspaceButton>
        ))}
        <WorkspaceButton
          variant="tertiary"
          className="ml-auto"
          aria-label="Close rewrite"
          disabled={accepting}
          onClick={onClose}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </WorkspaceButton>
      </div>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (typed) void ask({ instruction: typed });
        }}
      >
        <label htmlFor={`rewrite-${block.id}`} className="sr-only">
          What should change
        </label>
        <input
          id={`rewrite-${block.id}`}
          autoFocus
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder="Or say what to change, for example: lead with the risk"
          className="h-[30px] min-w-[220px] flex-1 rounded-card border border-ink-200 bg-paper-0 px-2.5 text-body-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
        />
        <WorkspaceButton type="submit" disabled={!typed || asking || accepting}>
          Rewrite
        </WorkspaceButton>
      </form>

      {asking ? (
        <p className="m-0 flex items-center gap-2 text-body-sm text-ai-ink">
          <Spinner className="h-3.5 w-3.5" />
          Rewriting: {instructionOf(step.request)}
        </p>
      ) : null}

      {step.kind === 'failed' ? (
        <div className="flex flex-col gap-2">
          <ErrorMessage error={step.error} />
          <div className="flex gap-2">
            <WorkspaceButton onClick={() => void ask(step.request)}>
              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
              Try again
            </WorkspaceButton>
          </div>
        </div>
      ) : null}

      {step.kind === 'answered' && step.result.issues.length > 0 ? (
        <Callout tone="warn" icon={AlertTriangle} role="alert">
          <ul className="m-0 flex list-none flex-col gap-1 p-0" data-testid="rewrite-issues">
            {step.result.issues.map((issue, i) => (
              <li key={i}>{rewriteIssueText(issue)}</li>
            ))}
          </ul>
          {step.result.proposal ? null : (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <WorkspaceButton onClick={() => void ask(step.request)}>
                <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                Try again
              </WorkspaceButton>
              <WorkspaceButton onClick={onEditByHand}>
                <PenLine className="h-3.5 w-3.5" aria-hidden="true" />
                Edit by hand
              </WorkspaceButton>
              <WorkspaceButton variant="tertiary" onClick={onClose}>
                Discard
              </WorkspaceButton>
            </div>
          )}
        </Callout>
      ) : null}

      {step.kind === 'answered' && step.result.proposal ? (
        <ProposalCompare
          current={block.text}
          proposal={step.result.proposal}
          instruction={step.result.instruction ?? instructionOf(step.request)}
          tokens={tokens}
          tokenSource={tokenSource}
          busy={accepting}
          accepting={accepting}
          onAccept={() =>
            void accept(
              step.result.proposal!,
              step.result.instruction ?? instructionOf(step.request),
            )
          }
          onTryAgain={() => void ask(step.request)}
          onDiscard={onClose}
          onOpenCitation={onOpenCitation}
        />
      ) : null}
      {acceptError ? <ErrorMessage error={acceptError} /> : null}
    </div>
  );
}
